import React, { useState } from "react";
import axios from "axios";
import {
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MoodIcon from "@mui/icons-material/Mood";
import MoodBadIcon from "@mui/icons-material/MoodBad";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";

const AICommentAnalyzer = ({ token, onGenerateResponse }) => {
  const [commentText, setCommentText] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  
  const [aiFeatures] = useState([
    "Sentiment Analysis",
    "Professional Tone",
    "Brand Protection",
    "Regional Detection",
  ]);

  const analyzeComment = async () => {
    if (!commentText.trim()) return;
    
    setIsAnalyzing(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ai/analyze-comment`,
        { commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
    } catch (error) {
      console.error("AI analysis failed", error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUseResponse = () => {
    if (onGenerateResponse) {
       onGenerateResponse(result.suggestedResponse);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Input Section */}
        <div className="flex-1 w-full">
          <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-4">
            Test the professional brand manager AI. Enter any comment (it could be a complaint, praise, or a scam claim) to see how the AI analyzes and rebuts it.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g., 'Mera package kab aayega?' or 'This product is a total scam, fake stuff!'"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="bg-white dark:bg-gray-800 rounded-2xl mb-4"
            sx={{
               '& .MuiOutlinedInput-root': {
                 borderRadius: '1rem',
               },
            }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={analyzeComment}
            disabled={isAnalyzing || !commentText.trim()}
            startIcon={
              isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <SmartToyIcon />
            }
            className="bg-primary-600 hover:bg-primary-700 py-3.5 rounded-2xl font-bold shadow-lg transform transition-all active:scale-95 normal-case"
          >
            {isAnalyzing ? "Professional Analysis in Progress..." : "Analyze & Draft Rebuttal"}
          </Button>

          <div className="mt-8">
            <Typography variant="caption" className="text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider block mb-3">
              AI Capabilities:
            </Typography>
            <div className="flex flex-wrap gap-2">
              {aiFeatures.map((f, i) => (
                <Chip key={i} label={f} size="small" className="bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-none font-medium" />
              ))}
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="flex-1 w-full">
           {!result && !isAnalyzing && (
             <Paper className="h-full min-h-[300px] flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800/40 border-dashed border-2 border-gray-200 dark:border-gray-700 rounded-3xl">
                <AutoAwesomeIcon sx={{ fontSize: 48 }} className="text-gray-200 dark:text-gray-700 mb-4" />
                <Typography className="text-gray-400 dark:text-gray-600 font-medium text-center">
                  Analyze a comment to see the AI's <br/> sentiment and suggested rebuttal here.
                </Typography>
             </Paper>
           )}

           {isAnalyzing && (
             <Paper className="h-full min-h-[300px] flex flex-col items-center justify-center p-8 bg-primary-50 dark:bg-primary-900/10 border-2 border-primary-100 dark:border-primary-900/30 rounded-3xl animate-pulse">
                <CircularProgress thickness={6} size={50} />
                <Typography className="text-primary-600 dark:text-primary-400 font-bold mt-4">
                  AI is thinking...
                </Typography>
             </Paper>
           )}

           {result && !isAnalyzing && (
             <div className="animate-in fade-in slide-in-from-right-4 duration-500">
               <Paper className={`p-6 rounded-3xl border-2 mb-6 ${
                 result.sentiment === 'positive' ? 'border-green-100 bg-green-50/50 dark:bg-green-900/10 dark:border-green-900/30' :
                 result.sentiment === 'negative' ? 'border-red-100 bg-red-50/50 dark:bg-red-900/10 dark:border-red-900/30' :
                 'border-blue-100 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-900/30'
               }`}>
                 <div className="flex items-center gap-3 mb-4">
                    {result.sentiment === 'positive' && <MoodIcon className="text-green-500" />}
                    {result.sentiment === 'negative' && <MoodBadIcon className="text-red-500" />}
                    {result.sentiment === 'neutral' && <SentimentSatisfiedIcon className="text-blue-500" />}
                    <Typography className="font-black uppercase tracking-widest text-xs">
                      {result.sentiment} Sentiment Detected
                    </Typography>
                 </div>
                 
                 <Divider className="mb-4 opacity-50" />
                 
                 <Typography variant="overline" className="text-gray-400 font-black block mb-2">Suggested Response</Typography>
                 <Typography className="text-gray-800 dark:text-gray-100 font-medium italic mb-6">
                   "{result.suggestedResponse}"
                 </Typography>

                 <Button 
                   variant="outlined" 
                   onClick={handleUseResponse}
                   className="rounded-xl normal-case font-bold border-gray-300 text-gray-700 dark:text-gray-300"
                 >
                   Copy to Clipboard
                 </Button>
               </Paper>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default AICommentAnalyzer;
